import {useCallback} from "react"

import {ApolloClient, ApolloLink, concat, DocumentNode, from, HttpLink, InMemoryCache} from "@apollo/client"
import {onError} from "@apollo/client/link/error"
import {RetryLink} from "@apollo/client/link/retry"
import {OptionsObject, useSnackbar} from "notistack"
import {v4 as uuidv4} from "uuid"

// Personalized type for the ApiFetchParam
type FetchParams = {
    url: string | undefined
    query: DocumentNode
    variables: object | undefined
    odmToken?: string
    type: "mutation" | "query"
    isApiKey?: boolean
    cache?: InMemoryCache
    customToken?: string
    restore?: boolean
    suppressErrorToast?: boolean
}

// Personalized type for the toaster options
interface CustomOptionsObject extends OptionsObject {
    type?: string
    title?: string
    code?: number
    id?: string
    createdAt: string
}

export function useApiFetch() {
    const {enqueueSnackbar} = useSnackbar()

    // Fonction fetchData pour exécuter une requête API
    const sendApiRequest = useCallback(
        async (params: FetchParams) => {
            const {url, query, variables, odmToken, type, isApiKey, cache, customToken, restore, suppressErrorToast} =
                params

            const token = localStorage.getItem("accessToken")

            // Returns the current date and time formatted in (dd MMM yyyy hh:mm).
            function createdAt(): string {
                const now = new Date()
                const formattedDate = new Intl.DateTimeFormat("fr-FR", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit"
                }).format(now)
                const formattedTime = new Intl.DateTimeFormat("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit"
                }).format(now)
                return `${formattedDate} ${formattedTime}`
            }

            const retryLink = new RetryLink({
                delay: {initial: 2000, max: 7, jitter: false},
                attempts: {max: 1}
            })

            const authMiddleware = new ApolloLink((operation, forward) => {
                const headers: Record<string, string> = {}
                if (isApiKey) {
                    headers.apiKey = customToken || token || ""
                } else {
                    const localRoleToken = odmToken || JSON.parse(localStorage.getItem("selectedRole") || "{}").token
                    headers.authorization = `Bearer ${token}`
                    headers.roleToken = localRoleToken
                }
                operation.setContext(({headers: existingHeaders = {}}) => ({
                    headers: {...existingHeaders, ...headers}
                }))
                return forward(operation)
            })

            const myCache = cache || new InMemoryCache({addTypename: false})

            if (restore) {
                myCache.restore({})
            }

            const errorLink = onError(({graphQLErrors, networkError}) => {
                if (suppressErrorToast) return
                if (graphQLErrors) {
                    graphQLErrors.forEach(({message, locations, path}) => {
                        console.log("GraphQl Error")
                        enqueueSnackbar({
                            type: "error",
                            title: "Error",
                            message: message,
                            id: uuidv4(),
                            createdAt: createdAt()
                        } as CustomOptionsObject)
                    })
                } else if (networkError) {
                    console.error(`Erreur serveur: ${networkError}`)
                    enqueueSnackbar({
                        type: "error",
                        title: "Error",
                        message: "Erreur Serveur",
                        code: 500,
                        id: uuidv4(),
                        createdAt: createdAt()
                    } as CustomOptionsObject)
                }
            })

            // Utility function to recursively remove __typename
            const removeTypename = (value: any): any => {
                if (value === null || value === undefined) {
                    return value
                }

                if (Array.isArray(value)) {
                    return value.map(removeTypename)
                }

                if (typeof value === "object" && Object.keys(value)?.length > 0) {
                    const newObj: any = {}
                    for (const key in value) {
                        if (key !== "__typename" && value.hasOwnProperty(key)) {
                            newObj[key] = removeTypename(value[key])
                        }
                    }
                    return newObj
                }

                return value
            }

            // Create the link that strips __typename from variables
            const cleanTypenameLink = new ApolloLink((operation, forward) => {
                if (operation.variables) {
                    operation.variables = removeTypename(operation.variables)
                }
                return forward(operation)
            })

            const client = new ApolloClient({
                cache: new InMemoryCache(),
                devtools: {enabled: import.meta.env.DEV},
                link: from([cleanTypenameLink, retryLink, errorLink, concat(authMiddleware, new HttpLink({uri: url}))])
            })

            try {
                let response
                if (type === "mutation") {
                    response = await client.mutate({mutation: query, variables: variables})
                } else {
                    response = await client.query({query: query, variables: variables})
                }
                return response
            } catch (error) {
                throw error
            }
        },
        [enqueueSnackbar]
    )

    return {sendApiRequest}
}
